import { Link, Outlet, useLocation, useMatchRoute } from '@tanstack/react-router';

import { cn } from '~/utils/cn';

interface Tab {
  to: string;
  label: string;
}

const TABS: Tab[] = [
  { to: '/platform/service-auth/users', label: 'Service Users' },
  { to: '/platform/service-auth/audit', label: 'Audit' },
  { to: '/platform/service-auth/discovery', label: 'Discovery' },
];

/**
 * Shell rendered around every Service Auth route. Hosts an inner tab nav
 * (Service Users / Audit / Discovery). The tabs are hidden on the user
 * detail page (`/users/$id`) so the detail page can occupy the full width.
 */
export function ServiceAuthLayout() {
  const location = useLocation();
  const matchRoute = useMatchRoute();

  // Hide tabs when viewing a single service-user (detail uses full-width breadcrumb header).
  const onDetail = !!matchRoute({
    to: '/platform/service-auth/users/$id',
    fuzzy: false,
  });

  return (
    // Settings card shell; the inner `space-y-6 p-6` keeps the padding so the
    // tab nav and sub-pages stay spaced. Matches the subsidiary-settings style guide.
    <div className="bg-card rounded-2xl shadow-sm">
      <div className="space-y-6 p-6">
        <div>
          <h2 className="text-lg leading-7 font-semibold">Service Auth</h2>
          <p className="text-muted-foreground text-sm leading-5">
            Manage OAuth2 client_credentials principals, credentials, and discovery documents.
          </p>
        </div>

        {!onDetail && (
          <div className="border-b">
            <nav className="-mb-px flex gap-6">
              {TABS.map((tab) => {
                const isActive = location.pathname.startsWith(tab.to);
                return (
                  <Link
                    key={tab.to}
                    to={tab.to}
                    className={cn(
                      'cursor-pointer border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                      isActive
                        ? 'border-primary text-foreground'
                        : 'text-muted-foreground hover:text-foreground border-transparent',
                    )}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        <Outlet />
      </div>
    </div>
  );
}
